// © Microsoft Corporation. All rights reserved.

import { AadData, AccessTokenStatus, AccountInfo, AppEnvironment, RegionScope, getAppEnvironment } from "@msnews/core";
import { AccountType, ConnectorNamespaces } from "@msnews/experiences-constants";
import { AcquireAccessTokenFailed, AcquireAccessTokenHandledFailure, AppError, EdgeChromiumAPIsNotAvailable, Severity } from "@msnews/diagnostics";

import { ExperiencesSharedStateConnector } from "@msnews/experiences-shared-state";
import { PageBase } from "@msnews/experiences-page-base";
import { Telemetry } from "@msnews/telemetry-contracts";
import { canUseDOM } from "@msnews/isomorphic-util";
import { getPageTimings } from "@msnews/diagnostics";

/**
 * chrome
 * The chrome API is not defined in Typescript
 */
declare global {
    interface Window { chrome: any; }
}

const authAccessTokenMap = {};

const AuthTokenSuccessData: AppError = {
    id: 35204,
    source: "AadAuthHelper.ts",
    severity: Severity.NoAlert
};

let isPageViewReady = false;

const MAX_RETRIES = 3;
const EXP_RETRY = 1000;

/** The aadToken QS key */
export const aadTokenQsKey = "stn";

/** The aadToken QS value */
export const aadTokenQsVal = "aad";

// More information re: specific errors here:
// https://microsoft.visualstudio.com/Edge/_git/chromium.src?path=%2Ftools%2Fmetrics%2Fhistograms%2Fedge_enums.xml&version=GBmaster&line=3770&lineEnd=3770&lineStartColumn=1&lineEndColumn=52&lineStyle=plain
export enum TokenFetchSecondaryErrors {
    /** No Error. */
    "kNoError" = 0,
    /** Token fetch operation requires user interaction */
    "kTokenFetchUserInteractionRequired" = 13,
    /** Authentication mechanism not supported by the client machine */
    "kTokenFetchProviderError" = 15,
    /** Authentication is possible, but it requires direct user interaction */
    "kInteractionRequired" = 24,
    /** Sovereignty is not supported in Edge. */
    "kUnsupportedSovereignty" = 60,
    /** Sovereignty detection failed for various reasons. */
    "kSovereigntyDetectionFailed" = 61
}

export enum InternalAuthError {
    /** Sovereignty error not handled by auth api */
    "UnsupportedSovereignty" = 0,
    /** Is CN domain */
    "CnDomain" = 1,
    /** Auth api is not available */
    "AuthApiUnavailable" = 2
}

/**
 * Gets a value to indicate if we are in an enterprise scenario
 * eg. whether we should append the AAD Token as part of the call to oneservice endpoints
 * The default value is false, so we don't need to wait until the value is explicitly set
 */
export function useEnterpriseCompliance(): boolean {
    // Obtain the current value of useEnterpriseCompliance from the ExperiencesSharedState connector
    const sharedStateConnector: ExperiencesSharedStateConnector = PageBase.getInstance().rootReducer.connector(ConnectorNamespaces.SharedState) as ExperiencesSharedStateConnector;
    return sharedStateConnector ? sharedStateConnector.getCurrentState().useEnterpriseCompliance : false;
}

/**
 * Gets a value to indicate if we should use enterprise domain
 * eg. whether we should use the Falcon domain vs. consumer domain for oneservice requests
 * The default value is false, so we don't need to wait until the value is explicitly set
 */
export function useEnterpriseDomain(): boolean {
    // Obtain the current value of useEnterpriseDomain from the ExperiencesSharedState connector
    const sharedStateConnector: ExperiencesSharedStateConnector = PageBase.getInstance().rootReducer.connector(ConnectorNamespaces.SharedState) as ExperiencesSharedStateConnector;
    return sharedStateConnector ? sharedStateConnector.getCurrentState().useEnterpriseDomain : false;
}

/**
 * Checks if aad token is invalid for user
 */
export function isInvalidAadToken(aadData: AadData): boolean {
    return !aadData || !aadData.isSuccess || !aadData.isValid;
}

export function waitForPageReadyAsync(): Promise<void> {
    if (isPageViewReady) {
        return Promise.resolve();
    }
    return new Promise(resolve => {
        const eventHandler = () => {
            isPageViewReady = true;
            window.removeEventListener("TelemetryPageViewNotificationEvent", eventHandler);
            resolve();
        };
        window.addEventListener("TelemetryPageViewNotificationEvent", eventHandler);
    });
}

function retriableError(platformErrorCode: number): boolean {
    if (!platformErrorCode) {
        return false;
    }

    const prefixErrorsHex = ["CAA3", "CAA7", "CAA8", "CAAD"];
    const specificRetriesHex = ["CAA20005", "CAA20008", "CAA10001"];

    // Convert decimal to hexadecimal and uppercase it
    const hexCode = ((platformErrorCode)>>>0).toString(16).toUpperCase();

    if (prefixErrorsHex.find(prefix => hexCode.startsWith(prefix)) !== undefined) {
        return true;
    }

    if (specificRetriesHex.includes(hexCode)) {
        return true;
    }

    return false;
}

/**
 * Fetches aad data to be used to call an authenticated API.
 * @param clientId The client id of the AAD application requesting access
 * @param scopeOrResource The scope or resource to request access for
 * @param friendlyName Friendly name for scope
 * @param allowInteraction Attempt to fetch the access token using the allowInteraction version of the API instead of the silent version
 * @param currTry Current nth retry, exits when we reach MAX_RETRIES
 * @param max_retry Max number of retries, defaults to MAX_RETRIES
 * @param wait Amount of time we exponentially wait between each retry
 */
export async function getAadDataWithRetry(clientId: string, scopeOrResource: string, friendlyName: string, allowInteraction = false, max_retry = MAX_RETRIES, currTry = 1, wait = EXP_RETRY): Promise<AadData> {
    return new Promise((resolve) => {
        getAadData(clientId, scopeOrResource, friendlyName, allowInteraction).then((result) => {
            // resolve early if token is returned succesfully
            if (result?.status == AccessTokenStatus.success) {
                return resolve(result);
            }

            // resolve anyway if we have exceeded max retries, leave caller of getAadData() to handle error & timeout.
            if (currTry >= max_retry) {
                return resolve(result);
            }

            // check if error code is retriable
            if (result?.errorInfo && retriableError(result.errorInfo.platform_error_code)) {
                // exponentially wait before each retry
                setTimeout(() => {
                    // recursively call and return function, add exponential time and increase try by 1
                    return resolve(getAadDataWithRetry(clientId, scopeOrResource, friendlyName, allowInteraction, max_retry, currTry + 1, wait * 2));
                }, wait);
            } else {
                // resolve anyway if errors are not retriable, leave caller of getAadData() to handle error & timeout.
                return resolve(result);
            }
        });
    });
}

/**
 * Fetches aad data to be used to call an authenticated API.
 * @param clientId The client id of the AAD application requesting access
 * @param scopeOrResource The scope or resource to request access for
 * @param friendlyName Friendly name for scope
 * @param allowInteraction Attempt to fetch the access token using the allowInteraction version of the API instead of the silent version
 */
export async function getAadData(clientId: string, scopeOrResource: string, friendlyName: string, allowInteraction = false): Promise<AadData> {
    const appEnvironment = getAppEnvironment();
    const accountInfo = await resolveAccountInfo(appEnvironment);

    // Currently unable to access auth apis from .cn domain due to sovereignty issues
    if (appEnvironment.IsCnDomain) {
        const cnError = {
            errorInfo: {
                internal_error_code: InternalAuthError.CnDomain
            },
            status: AccessTokenStatus.otherError
        };

        fireAuthTelemetry(null, cnError, friendlyName, false, "CN domain unsupported", AcquireAccessTokenHandledFailure);

        return cnError;
    }

    if (!accountInfo) {
        const unavailableError = {
            errorInfo: {
                internal_error_code: InternalAuthError.AuthApiUnavailable
            },
            status: AccessTokenStatus.otherError
        };

        fireAppError(EdgeChromiumAPIsNotAvailable, "Auth api not available", `Auth api not available for ${friendlyName}`);
        fireAuthTelemetry(null, unavailableError, friendlyName, false, "Auth api not available", AcquireAccessTokenHandledFailure);

        return unavailableError;
    }

    const { account_id, account_type, tenant_id } = accountInfo;

    // Catches unsupported sovereignties on .com domain (ie. Germany)
    if (account_type === AccountType.UNSUPPORTED_SOVEREIGNTY) {
        const sovereigntyError = {
            errorInfo: {
                internal_error_code: InternalAuthError.UnsupportedSovereignty
            },
            status: AccessTokenStatus.otherError
        };

        fireAuthTelemetry(tenant_id, sovereigntyError, friendlyName, false, "Edge unsupported sovereignty", AcquireAccessTokenHandledFailure);
        return sovereigntyError;
    }

    return new Promise((resolve) => {
        const tokenAcquireParameters = {
            account_id: account_id,
            account_type: account_type,
            client_id: clientId,
            scope_or_resource: scopeOrResource
        };

        const totalMarkerName = `Auth.AcquireAccessToken.${friendlyName}`;
        const startMarkerName = `${totalMarkerName}.Start`;
        const endMarkerName = `${totalMarkerName}.End`;
        const pageTimings = getPageTimings();
        const startPerfMarker = pageTimings[startMarkerName];
        const endPerfMarker = pageTimings[endMarkerName];
        const totalPerfMarker = pageTimings[totalMarkerName];
        const startTime = Math.round(performance.now());
        if (!startPerfMarker && !allowInteraction) {
            pageTimings[startMarkerName] = startTime;
        }

        const tokenAccessApi = allowInteraction ? window.chrome.authPrivate.acquireAccessTokenWithUserInteraction : window.chrome.authPrivate.acquireAccessTokenSilently;

        tokenAccessApi(tokenAcquireParameters, (acquireResult) => {
            if (!endPerfMarker && !allowInteraction) {
                const endTime = Math.round(performance.now());
                pageTimings[endMarkerName] = acquireResult.is_success ? endTime : -1;
                if (!totalPerfMarker) {
                    pageTimings[totalMarkerName] = acquireResult.is_success ? endTime - startTime : -1;
                }
            }

            let status = AccessTokenStatus.success;
            const authInfoObject = {
                ...acquireResult.error_info,
                authPrivateApi: allowInteraction ? "acquireAccessTokenWithUserInteraction" : "acquireAccessTokenSilently",
                resource: friendlyName
            };

            if (!acquireResult.is_success) {
                switch (acquireResult.error_info.secondary_error) {
                    // TODO: With the addition of retry logic, we might want to only fire apperrors if max amount of retries have been reached.
                    // Otherwise perhaps logging only a warning should be sufficient for the first X amount of retries.
                    case TokenFetchSecondaryErrors.kTokenFetchUserInteractionRequired:
                    case TokenFetchSecondaryErrors.kInteractionRequired:
                        status = AccessTokenStatus.mfaRequired;
                        fireAppError(AcquireAccessTokenHandledFailure, "Handled failure to get access token", JSON.stringify(authInfoObject));
                        fireAuthTelemetry(tenant_id, authInfoObject, friendlyName, false, "Handled failure to get access token", AcquireAccessTokenHandledFailure);
                        break;
                    default:
                        status = AccessTokenStatus.otherError;
                        fireAppError(AcquireAccessTokenFailed, "Failed to get access token", JSON.stringify(authInfoObject));
                        fireAuthTelemetry(tenant_id, authInfoObject, friendlyName, false, "Failed to get access token", AcquireAccessTokenFailed);
                }
            } else {
                waitForPageReadyAsync().then(() => {
                    if (!authAccessTokenMap[friendlyName]) {
                        authAccessTokenMap[friendlyName] = true;
                        fireAuthTelemetry(tenant_id, authInfoObject, friendlyName, true, "Access token fetch was successful", AuthTokenSuccessData);
                    }
                });
            }

            resolve({
                token: acquireResult.access_token,
                errorInfo: acquireResult.error_info,
                isSuccess: acquireResult.is_success,
                isValid: acquireResult.is_token_valid,
                status
            });
        });
    });
}

/**
 * Resolve account info promise
 * @param appEnvironment App Environment context
 */
export async function resolveAccountInfo(appEnvironment: AppEnvironment): Promise<AccountInfo> {
    if (!appEnvironment.AccountInfoPromise) {
        return null;
    }

    return await appEnvironment.AccountInfoPromise;
}

/**
 * Reads account info to check if current user is a GCC Mod account
 */
export async function isGccModUser(): Promise<boolean> {
    const appEnvironment = getAppEnvironment();
    const accountInfo = await resolveAccountInfo(appEnvironment);

    if (!accountInfo) {
        return false;
    }

    return accountInfo.region_scope === RegionScope.GCC_MODERATE;
}

/**
 * Fires app error
 * @param appError app error info
 * @param message message to log
 * @param customMessage message to include in property bag
 */
export function fireAppError(appError: AppError, message: string, customMessage: string): void {
    Telemetry.sendAppErrorEvent({
        ...appError,
        message,
        pb: {
            ...appError.pb,
            customMessage: customMessage
        }
    });
}

/**
 * Fires telemetry to auth table
 * @param tenantId tenant id
 * @param authInfo auth object to log
 * @param friendlyName friendly name of api
 * @param status auth status
 * @param message message to log
 * @param logData app "error" data
 */
export function fireAuthTelemetry(tenantId: string, authInfo: any, friendlyName: string, status: boolean, message: string, logData: AppError): void {
    Telemetry.sendAuthEvent({
        id: tenantId,
        status,
        name: friendlyName,
        info: {
            ...logData,
            message,
            pb: {
                ...logData.pb,
                customMessage: JSON.stringify(authInfo)
            }
        }
    });
}

/**
 * Returns string for X-AnchorMailbox route key
 */
export async function substrateCafeRouteKey(): Promise<string> {
    return getAppEnvironment().AccountInfoPromise.then((accountInfo) => {
        if (accountInfo && accountInfo.tenant_id) {
            if (accountInfo.oid) {
                return `Oid:${accountInfo.oid}@${accountInfo.tenant_id}`;
            }
            return `TID:${accountInfo.tenant_id}`;
        }

        return "";
    });
}

// invoking this method will ensure that pageView listeners are attached upon module loading
if (canUseDOM()) {
    waitForPageReadyAsync();
}